import { createSelector } from 'reselect';

const selectApiKeysDomain = state => state.ApiKeys;

// Basic selectors
const makeSelectApiKeys = () =>
  createSelector(selectApiKeysDomain, substate => substate);

const makeSelectApiKeysList = () =>
  createSelector(selectApiKeysDomain, substate => substate.apiKeys);

const makeSelectApiKeysLoading = () =>
  createSelector(selectApiKeysDomain, substate => substate.loading);

const makeSelectApiKeysError = () =>
  createSelector(selectApiKeysDomain, substate => substate.error);

const makeSelectApiKeysLastUpdated = () =>
  createSelector(selectApiKeysDomain, substate => substate.lastUpdated);

const makeSelectApiKeysTotal = () =>
  createSelector(selectApiKeysDomain, substate => substate.total);

// Utility selectors
const makeSelectApiKeyById = (keyId) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.find(key => key.id === keyId)
  );

const makeSelectApiKeysByPage = (pageId) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => key.pageId === pageId)
  );

const makeSelectApiKeysByRole = (roleId) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => key.roleId === roleId)
  );

const makeSelectApiKeysByStatus = (status) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => key.status === status)
  );

const makeSelectApiKeysActive = () =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => key.status === 'active')
  );

const makeSelectApiKeysInactive = () =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => key.status === 'inactive')
  );

const makeSelectApiKeysExpired = () =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key =>
      key.expiresAt && new Date(key.expiresAt) < new Date()
    )
  );

const makeSelectApiKeysExpiringSoon = (days = 7) =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + days);
      return substate.apiKeys.filter(key =>
        key.expiresAt &&
        new Date(key.expiresAt) > new Date() &&
        new Date(key.expiresAt) <= futureDate
      );
    }
  );

const makeSelectApiKeysSearch = (searchTerm) =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      if (!searchTerm) return substate.apiKeys;
      const term = searchTerm.toLowerCase();
      return substate.apiKeys.filter(key =>
        (key.name && key.name.toLowerCase().includes(term)) ||
        (key.description && key.description.toLowerCase().includes(term)) ||
        (key.pageName && key.pageName.toLowerCase().includes(term)) ||
        (key.roleName && key.roleName.toLowerCase().includes(term)) ||
        (key.key && key.key.toLowerCase().includes(term))
      );
    }
  );

const makeSelectApiKeysGroupedByPage = () =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      const grouped = {};
      substate.apiKeys.forEach(key => {
        const pageId = key.pageId;
        if (!grouped[pageId]) {
          grouped[pageId] = [];
        }
        grouped[pageId].push(key);
      });
      return grouped;
    }
  );

const makeSelectApiKeysGroupedByRole = () =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      const grouped = {};
      substate.apiKeys.forEach(key => {
        const roleId = key.roleId;
        if (!grouped[roleId]) {
          grouped[roleId] = [];
        }
        grouped[roleId].push(key);
      });
      return grouped;
    }
  );

const makeSelectApiKeysGroupedByStatus = () =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      const grouped = {};
      substate.apiKeys.forEach(key => {
        const status = key.status;
        if (!grouped[status]) {
          grouped[status] = [];
        }
        grouped[status].push(key);
      });
      return grouped;
    }
  );

const makeSelectApiKeysByPageAndRole = (pageId, roleId) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key =>
      key.pageId === pageId && key.roleId === roleId
    )
  );

const makeSelectApiKeysCountByPage = () =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      const countByPage = {};
      substate.apiKeys.forEach(key => {
        const pageId = key.pageId;
        countByPage[pageId] = (countByPage[pageId] || 0) + 1;
      });
      return countByPage;
    }
  );

const makeSelectApiKeysCountByRole = () =>
  createSelector(
    selectApiKeysDomain,
    substate => {
      const countByRole = {};
      substate.apiKeys.forEach(key => {
        const roleId = key.roleId;
        countByRole[roleId] = (countByRole[roleId] || 0) + 1;
      });
      return countByRole;
    }
  );

const makeSelectApiKeysWithMaskedKeys = () =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.map(key => ({
      ...key,
      key: key.key ? `${key.key.substring(0, 8)}...${key.key.substring(key.key.length - 4)}` : null
    }))
  );

const makeSelectApiKeysByCreatedDateRange = (startDate, endDate) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => {
      const createdAt = new Date(key.createdAt);
      return createdAt >= startDate && createdAt <= endDate;
    })
  );

const makeSelectApiKeysByLastUsedDateRange = (startDate, endDate) =>
  createSelector(
    selectApiKeysDomain,
    substate => substate.apiKeys.filter(key => {
      if (!key.lastUsedAt) return false;
      const lastUsedAt = new Date(key.lastUsedAt);
      return lastUsedAt >= startDate && lastUsedAt <= endDate;
    })
  );

export default makeSelectApiKeys;
export {
  makeSelectApiKeys,
  makeSelectApiKeysList,
  makeSelectApiKeysLoading,
  makeSelectApiKeysError,
  makeSelectApiKeysLastUpdated,
  makeSelectApiKeysTotal,
  makeSelectApiKeyById,
  makeSelectApiKeysByPage,
  makeSelectApiKeysByRole,
  makeSelectApiKeysByStatus,
  makeSelectApiKeysActive,
  makeSelectApiKeysInactive,
  makeSelectApiKeysExpired,
  makeSelectApiKeysExpiringSoon,
  makeSelectApiKeysSearch,
  makeSelectApiKeysGroupedByPage,
  makeSelectApiKeysGroupedByRole,
  makeSelectApiKeysGroupedByStatus,
  makeSelectApiKeysByPageAndRole,
  makeSelectApiKeysCountByPage,
  makeSelectApiKeysCountByRole,
  makeSelectApiKeysWithMaskedKeys,
  makeSelectApiKeysByCreatedDateRange,
  makeSelectApiKeysByLastUsedDateRange,
};
